import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box, Button, Stepper, Step, StepLabel, Typography, IconButton } from "@mui/material";

import SubscriptionPlanStep from "./SubscriptionPlanStep";
import BasicDetailsStep from "./BasicDetailsStep";
import ReviewStep from "./ReviewStep";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

import { getRegisterInputs } from "../../utils/inputs/register.inputs";
import { validateRegistrationFields } from "../../utils/validation/validation";
import { registerUser } from "../../services/api/auth";
import { showToast } from "../../store/slices/toastSlice";
import { ERROR_MESSAGES } from "../../constant/messages";
import { ROUTE_PATHS } from "../../routes/routePaths";

const RegisterStepper = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const steps = ["Subscription Plan", "Basic Details", "Review"];

  const initialFormState = {
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    contact: "",
    subscription_plan_id: "",
    plan_details: "",
  };

  const basicDetailState = { first_name: "", last_name: "", email: "", password: "", contact: "" };
  const companyDetailState = { company_name: "", gst_no: "", company_address: "", state_name: "", state_code: "" };

  const [registerData, setRegisterData] = useState(initialFormState);
  const [basicDetails, setBasicDetails] = useState(basicDetailState);
  const [companyDetails, setCompanyDetails] = useState(companyDetailState);

  const handleNext = () => {
    if (activeStep === 0 && !registerData.subscription_plan_id) {
      dispatch(
        showToast({
          message: ERROR_MESSAGES.AUTH.EMPTY_SUBSCRIPTION_PLAN,
          severity: "error",
        })
      );
      return;
    }

    if (activeStep === 1) {
      const validationResult = validateRegistrationFields(registerData);
      if (!validationResult.valid) {
        dispatch(
          showToast({ message: validationResult.message, severity: "error" })
        );
        return;
      }
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);


  const handleRegister = async (e) => {
    e.preventDefault();

    const validationResult = validateRegistrationFields(registerData);
    if (!validationResult.valid) {
      dispatch(
        showToast({ message: validationResult.message, severity: "error" })
      );
      return;
    }

    try {
      const data = await registerUser(registerData);
      if (data.responseCode === 201) {
        localStorage.setItem("registerSuccess", data.message);
        dispatch(showToast({ message: data.message, severity: "success" }));
        navigate(ROUTE_PATHS.PUBLIC_ROUTES.LOGIN);
      } else {
        dispatch(showToast({ message: data.message, severity: "error" }));
      }
    } catch (error) {
      dispatch(showToast({ message: error.message, severity: "error" }));
    }
  };

  const handleReset = () => {
    setRegisterData(initialFormState);
    setActiveStep(0);
  };

  return (
    <Box sx={{ width: "100%", position: "relative" }}>

      {/* 1. TOP NAV BACK ARROW (Matching Screenshot 2026-06-22 154258.png) */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        {activeStep > 0 ? (
          <IconButton onClick={handleBack} sx={{ p: 0, color: "#1C1E21" }}>
            <ArrowBackIosNewIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        ) : (
          <IconButton onClick={() => navigate(-1)} sx={{ p: 0, color: "#1C1E21" }}>
            <ArrowBackIosNewIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        )}
      </Box>

      {/* Meta Branding Element */}
      <Typography variant="body2" sx={{ color: "#1C1E21", fontWeight: "600", display: "flex", alignItems: "center", gap: 0.5, mb: 1, opacity: 0.8 }}>
        Rently
      </Typography>

      {/* 2. DYNAMIC FACEBOOK STEP HEADERS */}
      <Typography variant="h4" sx={{ fontWeight: "700", fontSize: "32px", color: "#1C1E21", letterSpacing: "-0.5px", mb: 1 }}>
        {activeStep === 0 && "Get started on Registration"}
        {activeStep === 1 && "Tell us about your Company"}
        {activeStep === 2 && "Review your submission"}
      </Typography>

      <Typography variant="body1" sx={{ color: "#606770", fontSize: "15px", mb: 4, lineHeight: "20px" }}>
        {activeStep === 0 && "Create an account to manage vendor registration and applications with ease."}
        {activeStep === 1 && "Provide corporate business details in accordance with compliance regulation systems."}
        {activeStep === 2 && "Please confirm that the details displayed below are correct before committing."}
      </Typography>

      {/* 3. HIDDEN PROGRESS TRACKER STEPPER ENGINE */}
      {/* We keep the Stepper rendering for state synchronization, but style it as a minimal, clean micro-bar */}
      <Stepper
        activeStep={activeStep}
        connector={null} // Removes ugly desktop line connectors
        sx={{
          mb: 4,
          display: "flex",
          gap: 1,
          "& .MuiStep-root": { padding: 0, flex: 1 },
          "& .MuiStepLabel-root": { padding: 0 },
          "& .MuiStepLabel-iconContainer": { display: "none" }, // Hides bulky step circles
          "& .MuiStepLabel-labelContainer": { width: "100%" }
        }}
      >
        {steps.map((label, index) => (
          <Step key={label}>
            <Box
              sx={{
                height: "4px",
                borderRadius: "2px",
                backgroundColor: index <= activeStep ? "#1877F2" : "#E4E6EB", // Facebook blue progress line indicator
                transition: "background-color 0.3s ease"
              }}
            />
          </Step>
        ))}
      </Stepper>

      {/* 4. DYNAMIC STEP DATA FORMS INPUT INNER BLOCK */}
      <Box sx={{ mt: 2 }}>
        {activeStep === 0 && (
          <SubscriptionPlanStep
            registerData={registerData}
            setRegisterData={setRegisterData}
            dispatch={dispatch}
          />
        )}

        {activeStep === 1 && (
          <BasicDetailsStep
            inputs={getRegisterInputs()}
            registerData={registerData}
            setRegisterData={setRegisterData}
          />
        )}

        {activeStep === 2 && <ReviewStep registerData={registerData} />}
      </Box>

      {/* 5. PRIMARY REGISTRATION BUTTON CONTROL FOOTER */}
      <Box sx={{ mt: 4 }}>
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            fullWidth
            onClick={handleNext}
            sx={{
              backgroundColor: "#1877F2",
              color: "#FFFFFF",
              textTransform: "none",
              fontSize: "17px",
              fontWeight: "500",
              height: "48px",
              borderRadius: "16px",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#166FE5", boxShadow: "none" },
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            type="submit"
            variant="contained"
            fullWidth
            onClick={handleRegister}
            sx={{
              backgroundColor: "#42B72A", // Signature Account Submit Green Theme
              color: "#FFFFFF",
              textTransform: "none",
              fontSize: "17px",
              fontWeight: "500",
              height: "48px",
              borderRadius: "6px",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#36A420", boxShadow: "none" },
            }}
          >
            Complete Registration
          </Button>
        )}
      </Box>

      <Typography variant="caption" sx={{ display: "block", color: "#737373", mt: 3, textAlign: "center", lineHeight: "16px" }}>
        By clicking the button above, you agree to our corporate platform vendor terms, conditions, data policies, and regulatory declarations.
      </Typography>
    </Box>
  );
};

export default RegisterStepper;
